import type { BackendRouteDefinition } from '../types';
import { acceptAragCaseRoute } from './acceptAragCase';
import { addPaymentInstrumentForFirmAsBuyerRoute } from './addPaymentInstrumentForFirmAsBuyer';
import { createFirmOnboardingFormLinkRoute } from './createFirmOnboardingFormLink';
import { createFirmOnboardingFormsRoute } from './createFirmOnboardingForms';
import { createPaymentPageRoute } from './createPaymentPage';
import { dailySummaryRoute } from './dailySummary';
import { deleteMatterRoute } from './deleteMatter';
import { markFlatFeeEarnedRoute } from './earnedFlatFees';
import { disablePaymentInstrumentRoute } from './disablePaymentInstrument';
import { getAllEmployeesWithAuthInfoRoute } from './getAllEmployeesWithAuthInfo';
import { getAllMerchantInfoForFirmRoute } from './getAllMerchantInfoForFirm';
import { getEmployeeRegistrationPageRoute } from './getEmployeeRegistrationPage';
import { findEmployeeTenantRoute } from './findEmployeeTenant';
import { getPaymentInstrumentsForFirmAsBuyerRoute } from './getPaymentInstrumentsForFirmAsBuyer';
import { getPaymentPagesAuthenticatedRoute } from './getPaymentPagesAuthenticated';
import {
  createFirmFormDefinitionRoute,
  getFirmFormDefinitionRoute,
  listFirmFormDefinitionsRoute,
  listFirmFormSubmissionsRoute,
  patchFirmFormDefinitionRoute,
  publishFirmFormDefinitionRoute,
} from './formDefinitions';
import { generateDocumentRoute } from './generateDocument';
import {
  addInvoiceTransactionAllocationRoute,
  changeInvoiceTransactionAllocationRoute,
  releaseInvoiceTransactionAllocationRoute,
} from './invoiceAllocations';
import {
  approveDraftInvoiceRoute,
  revertDraftInvoiceApprovalRoute,
} from './invoiceApprovals';
import {
  restoreVoidedInvoiceRoute,
  voidFinalizedInvoiceRoute,
} from './invoiceVoids';
import { generateGoogleAuthUrlRoute } from './generateGoogleAuthUrl';
import { generateMatterFollowUpEmailDraftRoute } from './generateMatterFollowUpEmailDraft';
import { getAllAragCasesForAttorneyRoute } from './getAllAragCasesForAttorney';
import { getEngagementLetterRoute } from './getEngagementLetter';
import { getAragCaseByCaseAssistNumberRoute } from './getAragCaseByCaseAssistNumber';
import { getAragCasesByMemberIdRoute } from './getAragCasesByMemberId';
import { getGoogleCredentialsRoute } from './getGoogleCredentials';
import { getOpenAragCaseRequestsRoute } from './getOpenAragCaseRequests';
import { loadAndFillTextTemplateRoute } from './loadAndFillTextTemplate';
import { logFrontendEventRoute } from './logFrontendEvent';
import {
  generateMicrosoftAuthUrlRoute,
  getMicrosoftCredentialsRoute,
  revokeMicrosoftCredentialsRoute,
  sendMicrosoftEmailRoute,
} from './microsoft';
import {
  cancelOpenAIChatRoute,
  createScoutConversationRoute,
  deleteScoutConversationRoute,
  getScoutConversationMessagesRoute,
  listScoutConversationsRoute,
  openaiChatNonStreamingRoute,
  openaiChatRoute,
  openaiChatStreamingRoute,
} from './openaiChat';
import { recordSignaturePortalViewRoute } from './recordSignaturePortalView';
import { revokeGoogleCredentialsRoute } from './revokeGoogleCredentials';
import { sendGoogleEmailRoute } from './sendGoogleEmail';
import { saveAchPaymentMethodRoute } from './saveAchPaymentMethod';
import { saveEngagementLetterSignatureRoute } from './saveEngagementLetterSignature';
import { saveManualEngagementLetterSignatureRoute } from './saveManualEngagementLetterSignature';
import { sendEmployeeInviteRoute } from './sendEmployeeInvite';
import { sendEmployeePasswordResetLinkRoute } from './sendEmployeePasswordResetLink';
import { sharedRoutes } from './shared';
import {
  getTalkrouteSettingsRoute,
  listTalkrouteVirtualNumbersRoute,
  saveTalkrouteSettingsRoute,
  syncTalkrouteCommunicationEventsRoute,
} from './talkroute';
import {
  getScopedTypesenseKeyRoute,
  performInitialTypesenseSyncRoute,
} from './typesense';
import { updateEmployeeRoute } from './updateEmployee';
import { signIntoAragRoute } from './signIntoArag';
import { signOutOfAragRoute } from './signOutOfArag';
import { syncLawPayTransactionsRoute } from './syncLawPayTransactions';
import {
  classifyLateHistoricalTransactionRoute,
  adjustMatterInvoiceBalanceRoute,
  getMatterAmountDueRoute,
  getMatterInvoiceBalanceRoute,
  createFirmTransactionRoute,
  deleteFirmTransactionRoute,
  failUnconsumedRetainerTransferRoute,
  refreshUnconsumedRetainerTransferRoute,
  unlinkFirmFinancialInstitutionRoute,
  updateFirmTransactionRoute,
} from './transactions';

export const routeTable = {
  ...sharedRoutes,
  // These workflows stay callable-first in S1-WB1; the separate modules make
  // each later transport flip a single-file change for the owning work block.
  createPaymentPage: createPaymentPageRoute,
  saveAchPaymentMethod: saveAchPaymentMethodRoute,
  createFirmOnboardingForms: createFirmOnboardingFormsRoute,
  createFirmOnboardingFormLink: createFirmOnboardingFormLinkRoute,
  addPaymentInstrumentForFirmAsBuyer: addPaymentInstrumentForFirmAsBuyerRoute,
  disablePaymentInstrument: disablePaymentInstrumentRoute,
  getAllEmployeesWithAuthInfo: getAllEmployeesWithAuthInfoRoute,
  getEmployeeRegistrationPage: getEmployeeRegistrationPageRoute,
  findEmployeeTenant: findEmployeeTenantRoute,
  listFirmFormDefinitions: listFirmFormDefinitionsRoute,
  listFirmFormSubmissions: listFirmFormSubmissionsRoute,
  createFirmFormDefinition: createFirmFormDefinitionRoute,
  getFirmFormDefinition: getFirmFormDefinitionRoute,
  patchFirmFormDefinition: patchFirmFormDefinitionRoute,
  publishFirmFormDefinition: publishFirmFormDefinitionRoute,
  // S2-WB2 (PRA-788) — Google Workspace OAuth + email flipped from callable
  // to api transport. The OAuth callback (`exchangeGoogleAuthCode`) lives in
  // api-webhooks-node and is not invoked from this app, so it does not need
  // a route-table entry here.
  generateGoogleAuthUrl: generateGoogleAuthUrlRoute,
  getGoogleCredentials: getGoogleCredentialsRoute,
  revokeGoogleCredentials: revokeGoogleCredentialsRoute,
  sendGoogleEmail: sendGoogleEmailRoute,
  // Phase 5 / Sub-phase 2 / S2-WB1: Microsoft Graph integration migrated to
  // `api-core-node` (the OAuth callback also lives on `api-core-node` so it
  // gets the warm-start `minInstances: 1` budget — see
  // `apps/firms/src/backend/routes/microsoft.ts` for the rationale).
  generateMicrosoftAuthUrl: generateMicrosoftAuthUrlRoute,
  getMicrosoftCredentials: getMicrosoftCredentialsRoute,
  revokeMicrosoftCredentials: revokeMicrosoftCredentialsRoute,
  sendMicrosoftEmail: sendMicrosoftEmailRoute,
  // S3-WB1 (PRA-789) — ARAG session + read callables flipped to api
  // transport. S3-WB2 (PRA-790) added `acceptAragCase` as the first ARAG
  // mutation route, wrapped in durable Firestore idempotency so a
  // double-clicked Accept button doesn't post twice to ARAG.
  signIntoArag: signIntoAragRoute,
  signOutOfArag: signOutOfAragRoute,
  getOpenAragCaseRequests: getOpenAragCaseRequestsRoute,
  getAragCaseByCaseAssistNumber: getAragCaseByCaseAssistNumberRoute,
  getAragCasesByMemberId: getAragCasesByMemberIdRoute,
  getAllAragCasesForAttorney: getAllAragCasesForAttorneyRoute,
  acceptAragCase: acceptAragCaseRoute,
  // Phase 4 long-running endpoints. Each owns its own module so the Sub-phase 5
  // transport-flip work blocks (S5-WB5, S5-WB7, S5-WB8) edit a single file.
  generateDocument: generateDocumentRoute,
  openaiChat: openaiChatRoute,
  openaiChatNonStreaming: openaiChatNonStreamingRoute,
  openaiChatStreaming: openaiChatStreamingRoute,
  cancelOpenAIChat: cancelOpenAIChatRoute,
  listScoutConversations: listScoutConversationsRoute,
  getScoutConversationMessages: getScoutConversationMessagesRoute,
  createScoutConversation: createScoutConversationRoute,
  deleteScoutConversation: deleteScoutConversationRoute,
  // PRA-825 (the Phase 6 PRE-1 prerequisite): the
  // `generateMatterFollowUpEmailDraft` straggler flipped to api transport on
  // `api-longrun-node`. The placement matches its OpenAI-flavored siblings.
  generateMatterFollowUpEmailDraft: generateMatterFollowUpEmailDraftRoute,
  // PRA-798 (Phase 5 / Sub-phase 7 / S7-WB1): LawPay sync flipped to api
  // transport. The original callable remains in `cloudFunctions/` until the
  // S9-WB2 retirement sweep.
  syncLawPayTransactions: syncLawPayTransactionsRoute,
  createFirmTransaction: createFirmTransactionRoute,
  markFlatFeeEarned: markFlatFeeEarnedRoute,
  updateFirmTransaction: updateFirmTransactionRoute,
  failUnconsumedRetainerTransfer: failUnconsumedRetainerTransferRoute,
  refreshUnconsumedRetainerTransfer: refreshUnconsumedRetainerTransferRoute,
  deleteFirmTransaction: deleteFirmTransactionRoute,
  classifyLateHistoricalTransaction: classifyLateHistoricalTransactionRoute,
  adjustMatterInvoiceBalance: adjustMatterInvoiceBalanceRoute,
  getMatterAmountDue: getMatterAmountDueRoute,
  getMatterInvoiceBalance: getMatterInvoiceBalanceRoute,
  unlinkFirmFinancialInstitution: unlinkFirmFinancialInstitutionRoute,
  addInvoiceTransactionAllocation: addInvoiceTransactionAllocationRoute,
  changeInvoiceTransactionAllocation: changeInvoiceTransactionAllocationRoute,
  releaseInvoiceTransactionAllocation: releaseInvoiceTransactionAllocationRoute,
  approveDraftInvoice: approveDraftInvoiceRoute,
  revertDraftInvoiceApproval: revertDraftInvoiceApprovalRoute,
  voidFinalizedInvoice: voidFinalizedInvoiceRoute,
  restoreVoidedInvoice: restoreVoidedInvoiceRoute,
  // S5-WB1 (PRA-792): three firms-side payment read callables flipped to
  // api transport. The original callables remain in `cloudFunctions/` until
  // the S9-WB2 retirement sweep.
  getAllMerchantInfoForFirm: getAllMerchantInfoForFirmRoute,
  getPaymentPagesAuthenticated: getPaymentPagesAuthenticatedRoute,
  getPaymentInstrumentsForFirmAsBuyer: getPaymentInstrumentsForFirmAsBuyerRoute,
  // S4-WB1 (PRA-791) — engagement-letter signature portal flipped to api
  // transport. The signature portal is public-invoker (unauthenticated
  // clients clicking the email link), so the corresponding api-core-node
  // routes are registered with `auth: off` / `appCheck: off`.
  getEngagementLetter: getEngagementLetterRoute,
  recordSignaturePortalView: recordSignaturePortalViewRoute,
  saveEngagementLetterSignature: saveEngagementLetterSignatureRoute,
  // PRA-826 — firm-side authenticated route for recording a manual
  // engagement-letter signature (firm employee uploads a physically-signed
  // PDF on behalf of the client). See the file for why this is separate from
  // the public `saveEngagementLetterSignature` route.
  saveManualEngagementLetterSignature: saveManualEngagementLetterSignatureRoute,
  // S6-WB2 (PRA-795): three firms employee-management callables flipped to
  // api transport. The original callables remain in `cloudFunctions/` until
  // the S9-WB2 retirement sweep.
  sendEmployeeInvite: sendEmployeeInviteRoute,
  sendEmployeePasswordResetLink: sendEmployeePasswordResetLinkRoute,
  updateEmployee: updateEmployeeRoute,
  deleteMatter: deleteMatterRoute,
  // S7-WB3 (PRA-800): Typesense callables flipped to api transport.
  // `getScopedTypesenseKey` is on api-core-node (hot path); the
  // long-running `performInitialTypesenseSync` is on api-longrun-node.
  getScopedTypesenseKey: getScopedTypesenseKeyRoute,
  performInitialTypesenseSync: performInitialTypesenseSyncRoute,
  // PRA-828 — Talkroute has no API-key webhook payload in its attached docs,
  // so firms can manually poll recent calls/texts into communication events.
  listTalkrouteVirtualNumbers: listTalkrouteVirtualNumbersRoute,
  getTalkrouteSettings: getTalkrouteSettingsRoute,
  saveTalkrouteSettings: saveTalkrouteSettingsRoute,
  syncTalkrouteCommunicationEvents: syncTalkrouteCommunicationEventsRoute,
  // S8-WB1 (PRA-802): the last three firms-side Phase-5 callables flipped
  // to api transport. The original callables remain in `cloudFunctions/`
  // until the S9-WB2 retirement sweep.
  logFrontendEvent: logFrontendEventRoute,
  loadAndFillTextTemplate: loadAndFillTextTemplateRoute,
  dailySummary: dailySummaryRoute,
} as const satisfies Record<string, BackendRouteDefinition>;
